// @ts-strict-ignore
import {AnyAttrs, AttrsSchema, Component} from 'panel';
import produce from 'immer';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {PlanBuilderState} from 'media/js/pricing/plan-builder-2023/types';
import {formatDate, toCurrency} from 'media/js/pricing/plan-builder-2023/common/util';
import {
  BillingCredit,
  BillingCreditState,
  BillingFrequency,
  BreakdownLineItem,
  CurrentPlanBreakdownRow,
  CustomerType,
  ExperimentalCurvePoint,
  PricingCurvePackage,
  PricingCurveVariant,
} from 'media/js/pricing/api/types';
import type {PlanBuilder} from 'media/js/pricing/plan-builder-2023/index';
import {TrackingEvents} from 'media/js/pricing/tracking';
import {numberInUSD} from 'common/util';
import {AgreementLine} from 'common/types/app-api/billing_bump/types';

import {template} from './template';

type RightPaneState = any;

const CURRENT_ACCOUNT_BALANCE_INFO_TEXT = `Can include any unpaid invoices, additional data costs from the previous month, and/or credits from plan conversions.`;
const CHANGE_FEE_INFO_TEXT = `Early conversion of plan.`;

export const transformCancelledLineItem = (shortDescription) =>
  `Cancelled ${shortDescription.replace(`New `, ``).replace(`Prorated `, ``).replace(`Previous `, ``)}`;

// NOTE: The compareFunction argument to sort returns -1 if its first argument is less than
// its second argument, and 1 if its first argument is greater than its second argument
//
// We order breakdown lines based on the following rules:
// (The comparison function here could be shorter, but I'd rather be explicit with the ordering rules)
//
const breakdownLinesCompareFn = (firstEl, secondEl): number => {
  // This is factored out of getBreakdownLines for cognitive complexity reasons
  // Rebates are always ordered before non-rebates
  //
  if (firstEl.description.includes(`Rebate`) && !secondEl.description.includes(`Rebate`)) {
    return -1;
  }
  if (!firstEl.description.includes(`Rebate`) && secondEl.description.includes(`Rebate`)) {
    return 1;
  }
  // These two lines are either both rebates or two non-rebates
  //
  // Plans are ordered before packages
  //
  if (firstEl.description.includes(`Plan`) && !secondEl.description.includes(`Plan`)) {
    return -1;
  }
  if (!firstEl.description.includes(`Plan`) && secondEl.description.includes(`Plan`)) {
    return 1;
  }
  // We are comparing two non-plans (thus, packages) that are either both rebates or non-rebates
  // In this case, we want the descriptions to be sorted in alphabetical order
  //
  return firstEl.description < secondEl.description ? -1 : 1;
};

export const SALES_TAX_DESCRIPTION = `Sales Tax`;

export class RightPane extends Component<RightPaneState, AnyAttrs, PlanBuilderState, PlanBuilder> {
  static get attrsSchema(): AttrsSchema<AnyAttrs> {
    return {
      ...super.attrsSchema,
      'mp-2023-style': {
        type: `boolean`,
        description: `flag to check whether page should apply new 2023 stying`,
      },
    };
  }

  get config() {
    return {
      template,
      defaultState: {},
      helpers: {
        getValidBillingCredits: (): BillingCredit[] => {
          return this.app.state.customerInfo.orgInfo.billingCredits.filter(
            (credit) => credit.state === BillingCreditState.VALID,
          );
        },
        getCurrentPlanDateString: (): string => {
          const {orgInfo} = this.state.customerInfo;
          const startDate = new Date(orgInfo.eventsBillingPeriod.startDate);
          const endDate = new Date(orgInfo.eventsBillingPeriod.endDate);
          return `${formatDate(startDate)} - ${formatDate(endDate)}`;
        },
        isOverMax: (): boolean => {
          return (
            this.helpers.planMaxCap() !== undefined &&
            this.app.state.selectedUpdate.planChanges.cap > this.helpers.planMaxCap()
          );
        },
        planMaxCap: (): number | undefined => {
          const currentCurve = this.app.helpers.getCurrentPricingCurve();
          return currentCurve && Math.max(...Object.values(currentCurve.points).map((p) => p.cap));
        },
        getAgreementCurveId: (): number | undefined => {
          const currentCurve = this.app.helpers.getCurrentPricingCurve();
          return currentCurve.agreementGroupId;
        },
        getPlanBreakdownFromAgreements: () => {
          // TODO: move plan frequency calculate from frontend to backend to allow code re-use
          let planFrequency = `Monthly`;
          let basePlanAgreementLine = <AgreementLine>{};
          let netPrice = 0.0;
          for (const agreement of this.state.customerInfo.orgInfo.agreements) {
            agreement.agreement_lines.forEach((agreementLine) => {
              if (agreementLine.buyable_type === `BASE_FEATURE_PLAN`) {
                basePlanAgreementLine = agreementLine;

                if (agreement.name.includes(`Annual`)) {
                  planFrequency = `Annual`;
                }
              } else {
                netPrice += parseFloat(agreementLine.net_price);
              }
            });
          }

          const [startYear, startMonth, startDay] = basePlanAgreementLine.start_date.split(`-`);
          const startDate = new Date(parseInt(startYear), parseInt(startMonth) - 1, parseInt(startDay));
          const formattedStartDate = formatDate(startDate);
          const [endYear, endMonth, endDay] = basePlanAgreementLine.end_date.split(`-`);
          const endDate = new Date(parseInt(endYear), parseInt(endMonth) - 1, parseInt(endDay));
          const formattedEndDate = formatDate(endDate);

          return [
            {
              dateString: `${planFrequency} | ${formattedStartDate} - ${formattedEndDate}`,
              description: `${basePlanAgreementLine.buyable_name}`,
              price: `${numberInUSD(netPrice)}`,
            },
          ];
        },
        getCurrentPlanBreakdown: (): CurrentPlanBreakdownRow[] => {
          const {orgInfo} = this.state.customerInfo;
          let breakdownRows: CurrentPlanBreakdownRow[] = [];

          if (orgInfo.isOnBump) {
            return this.helpers.getPlanBreakdownFromAgreements();
          }

          if (orgInfo.legacyPlanDescription) {
            breakdownRows = orgInfo.legacyPlanDescription;

            if (orgInfo.isExperimentPlan) {
              return this.helpers.experimentalPlanDescription(breakdownRows);
            }
            // If not experimental plan, return legacy description as is
            return breakdownRows;
          }

          breakdownRows.push({
            description: this.app.helpers.getSelectedCapString(),
            price: toCurrency(this.helpers.getCurrentPricingCurvePoint()?.price),
          });

          orgInfo.currentPlanState.packageIds.forEach((packageId) => {
            breakdownRows.push({
              description: this.helpers.getPackageName(packageId),
              price: toCurrency(this.helpers.getCurrentPackagePrice(packageId)),
            });
          });

          const billingFrequency =
            orgInfo.currentPlanState.billingFrequency === BillingFrequency.ANNUAL ? `Annual` : `Monthly`;

          const dateString = `${billingFrequency}: ${this.helpers.getCurrentPlanDateString()}`;
          breakdownRows.push({dateString});

          return breakdownRows;
        },
        experimentalPlanDescription: (breakdownRows: CurrentPlanBreakdownRow[]): CurrentPlanBreakdownRow[] => {
          // For experimental plan, continue to generate description
          breakdownRows = breakdownRows.filter((row) => !row.description.includes(`People`));
          breakdownRows.forEach((row, index) => {
            // Only show date string as last entry
            if (index !== breakdownRows.length - 1) {
              row.dateString = undefined;
            }
          });

          const {currentPlanState} = this.state.customerInfo.orgInfo;
          const {cap, curveVariant, customerType} = currentPlanState;

          // If "paid" plan, create curve variant and cap string
          if (customerType === CustomerType.PREMIUM_PRICING_2023) {
            const index = breakdownRows.findIndex((rows) => rows.description.includes(`Events`));

            breakdownRows = produce(breakdownRows, (draft) => {
              draft[index].description = `${
                curveVariant === PricingCurveVariant.GROWTH ? `Growth` : `Enterprise`
              } ${this.app.helpers.abbreviateNumber(cap)} events`;
            });
          }

          return breakdownRows;
        },
        getCurrentPricingCurvePoint: (): ExperimentalCurvePoint => {
          const {currentPlanState} = this.state.customerInfo.orgInfo;
          return this.app.helpers.getCurrentPricingCurve()?.points[currentPlanState.cap];
        },
        getCurrentPackagePrice: (packageId: number): number => {
          const featurePackage: PricingCurvePackage = this.app.helpers
            .getCurrentPricingCurve()
            ?.packages.find((featurePkg) => featurePkg.id === packageId);
          const pointPrice = this.helpers.getCurrentPricingCurvePoint()?.price;
          return !pointPrice ? 0 : pointPrice * featurePackage.basePlanPct;
        },

        getBreakdownLines: (): BreakdownLineItem[] => {
          if (!this.app.state.amountDueInfo) {
            return [];
          }
          let breakdownLines = [];
          let changeFeeLine,
            creditFromPriorPlansLine,
            salesTaxLine = null;
          if (this.app.state.amountDueInfo.currentBalance) {
            breakdownLines.push({
              amount: toCurrency(this.app.state.amountDueInfo.currentBalance),
              description: `Current Account Balance`,
              infoText: CURRENT_ACCOUNT_BALANCE_INFO_TEXT,
            });
          }
          // If customer is on an annual plan, show them "remaining plan term" instead of "new plan term"
          //
          const isAnnualPlan =
            this.state.customerInfo.orgInfo.currentPlanState.billingFrequency === BillingFrequency.ANNUAL;
          breakdownLines.push({
            description: `${isAnnualPlan ? `Remaining` : `New`} Plan Term: ${
              this.app.helpers.isAnnualDowngrade()
                ? // Annual downgrades should show the current plan date string here
                  this.helpers.getCurrentPlanDateString()
                : this.app.helpers.getBillingPeriodString()
            }`,
          });
          // The indented breakdown lines may need to be displayed in a different order than they
          // come in the response, so we need a buffer to hold them until they are sorted
          //
          let indentedBreakdownLines = [];
          this.app.state?.amountDueInfo?.lines?.forEach((line) => {
            if (line.description === `Change Fee`) {
              changeFeeLine = line;
            } else if (line.description.startsWith(`Credit from`)) {
              creditFromPriorPlansLine = line;
            } else if (line.description === SALES_TAX_DESCRIPTION) {
              salesTaxLine = line;
              // Credits do not have a description and are included in the lines;
              // TODO @illirik: Come up with a better way to handle this
            } else if (line.description.length > 0) {
              let amount: number = parseFloat(line.amount);
              let description: string = line.description;
              if (line.state === `Cancelled`) {
                // Cancelled line items should be presented as refunds
                //
                amount *= -1;
                // The short description does not reflect state, so let's clean up
                // the string here to indicate that it is cancelled
                //
                // We should also remove the terms `New` and `Prorated` to clean up this description
                //
                description = transformCancelledLineItem(description);
              }
              indentedBreakdownLines.push({
                amount: toCurrency(amount),
                description,
                textIsGrey: true,
                indent: true,
              });
            }
          });
          // We need to reorder the indented breakdown lines so that they conform with design's requirements
          //
          indentedBreakdownLines = indentedBreakdownLines.sort(breakdownLinesCompareFn);
          breakdownLines = breakdownLines.concat(indentedBreakdownLines);
          const creditsApplied = this.app.state.amountDueInfo?.creditsApplied || 0;
          if (creditsApplied) {
            breakdownLines.push({
              // Credits applied come in as a positive amount, so we need to negate it to show as a negative line item
              amount: toCurrency(-1 * creditsApplied),
              description: `Mixpanel Credits`,
              numberIsGreen: true,
              infoText:
                `Your credits will be applied each month until they are used up or expire. ` +
                `Remaining credits after your purchase: ${toCurrency(
                  this.helpers.getValidBillingCredits().reduce((acc, c) => acc + c.remainingBalance, 0) -
                    creditsApplied,
                )}`,
            });
          }
          if (changeFeeLine) {
            breakdownLines.push({
              amount: toCurrency(parseFloat(changeFeeLine.amount)),
              description: `Change Fee`,
              infoText: CHANGE_FEE_INFO_TEXT,
            });
          }
          if (creditFromPriorPlansLine) {
            breakdownLines.push({
              amount: toCurrency(parseFloat(creditFromPriorPlansLine.amount)),
              description: creditFromPriorPlansLine.description,
            });
          }
          if (salesTaxLine) {
            breakdownLines.push({
              amount: toCurrency(parseFloat(salesTaxLine.amount)),
              description: SALES_TAX_DESCRIPTION,
            });
          }
          return breakdownLines;
        },

        // Misc
        getPackageName: (packageId): string => {
          const featurePackage: PricingCurvePackage = this.app.helpers
            .getCurrentPricingCurve()
            ?.packages.find((featurePkg) => featurePkg.id === packageId);
          return featurePackage?.name;
        },
        showAnnualSaving: (): boolean => {
          // Annual savings only make sense in the context of growth-monthly
          return (
            this.app.state.pricingCurves.length > 0 &&
            this.app.helpers.getSelectedPlanState().curveVariant === PricingCurveVariant.GROWTH
          );
        },
        getCurveVariantLabel: (): string => {
          if (this.app.helpers.getSelectedPlanState().curveVariant === PricingCurveVariant.GROWTH) {
            return `Growth`;
          }

          return `Enterprise`;
        },
        getAnnualSavingsCta: (): string => {
          const {cap: selectedCap, billingFrequency} = this.app.helpers.getSelectedPlanState();

          // TODO (Pricing 2023) - number index -> named field
          const annualPricePoint = this.app.state.pricingCurves[1].points[selectedCap];
          const monthlyPricePoint = this.app.state.pricingCurves[0].points[selectedCap];
          const monthlySavingCost = (monthlyPricePoint?.price || 0) - (annualPricePoint?.price || 0) / 12;

          if (billingFrequency === BillingFrequency.ANNUAL) {
            return `You're saving ${numberInUSD(monthlySavingCost * 12, {
              maximumFractionDigits: 0,
            })}/yr on annual billing`;
          }

          return `Save ${numberInUSD(monthlySavingCost, {
            maximumFractionDigits: 0,
          })}/mo on annual billing`;
        },
        getStartOrRenewalInfo: () => {
          const {startDate} = this.app.helpers.calculateBillingPeriod();
          const startsOrRenews = this.app.helpers.isAnnualDowngrade() ? `Starts` : `Renews`;
          const date = this.app.helpers.isAnnualDowngrade() ? formatDate(startDate) : this.app.helpers.getRenewalDate();
          return `${startsOrRenews}: ${this.app.helpers.isReady() ? date : ``}`;
        },
        trackContactSalesClicked: () => {
          this.app.helpers.trackEventWithPlanProps(TrackingEvents.ContactUs, {
            'Contact Us Source': window.location.href || `Plan Builder`,
          });
        },
        trackContactSupportClicked: () => {
          this.app.helpers.trackEventWithPlanProps(TrackingEvents.ContactSupport);
        },
      },
    };
  }

  public connectedCallback() {
    super.connectedCallback();
  }
}

export const RightPaneEl = defineAndMakeJsxFactory(`right-pane-2023`, RightPane);
